import * as React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import {
  describeCron,
  entityLabel,
  eventLabel,
  PlatformEventType,
  scheduleToUtcCron,
  timeZoneOptions,
  TriggerSourceType,
  weekdayName,
  type SchedulePreset,
} from "@/shared/domain/triggers";
import {
  Checkbox,
  Combobox,
  Field,
  FormSection,
  RadioGroup,
  Radio,
  Select,
  TextInput,
  type SelectItem,
} from "@/shared/foundation/primitives";

import { TriggerConditionBuilder } from "../../Trigger/TriggerConditionBuilder";
import { useTriggerConditionDefinitions } from "../../Trigger/useTriggerConditionDefinitions";
import {
  conditionToValues,
  valuesToCondition,
  type ConditionValues,
} from "../conditionValues";
import {
  changesOfferedFor,
  isSourceComplete,
  TRIGGER_FORM_ENTITIES,
  type TriggerFormEntity,
} from "../formScope";
import {
  type EventSubscriptionValues,
  type ScheduleValues,
  type TriggerFormValues,
} from "../triggerFormSchema";
import { useFormTemplateCatalog } from "../useFormTemplateCatalog";

import styles from "./sections.module.css";

const ENTITY_ITEMS: readonly SelectItem<TriggerFormEntity>[] =
  TRIGGER_FORM_ENTITIES.map((entity) => ({
    value: entity,
    label: entityLabel(entity),
  }));

const PRESET_ITEMS: readonly SelectItem<SchedulePreset>[] = [
  { value: "hourly", label: "Hour" },
  { value: "daily", label: "Day" },
  { value: "weekly", label: "Week" },
  { value: "custom", label: "Custom schedule" },
];

const WEEKDAY_ITEMS: readonly SelectItem<number>[] = [1, 2, 3, 4, 5, 6, 7].map(
  (weekday) => ({ value: weekday, label: weekdayName(weekday) }),
);

/** Whether the trigger watches for changes or runs on a clock. */
function RunModeField() {
  const { control } = useFormContext<TriggerFormValues>();

  return (
    <div data-testid="trigger-run-mode">
      <Controller
        control={control}
        name="firesOn.type"
        render={({ field }) => (
          <RadioGroup<TriggerSourceType>
            appearance="card"
            size="md"
            aria-label="What makes this trigger fire"
            value={field.value}
            onValueChange={field.onChange}
          >
            <Radio
              value={TriggerSourceType.Events}
              label="On a change"
              description="Fire when the data you name changes."
            />
            <Radio
              value={TriggerSourceType.Schedule}
              label="On a schedule"
              description="Fire on a repeating clock time."
            />
          </RadioGroup>
        )}
      />
    </div>
  );
}

/** Which kind of thing the trigger watches. */
function EntityField() {
  const { control } = useFormContext<TriggerFormValues>();

  return (
    <div data-testid="trigger-entity-field">
      <Controller
        control={control}
        name="firesOn.entity"
        render={({ field, fieldState }) => (
          <Select<TriggerFormEntity>
            items={ENTITY_ITEMS}
            value={field.value}
            onValueChange={(entity) => {
              if (entity !== null) {
                field.onChange(entity);
              }
            }}
            size="lg"
            label={<span className={styles.blockLabel}>Entity</span>}
            description="What the trigger watches. One entity per trigger."
            descriptionPlacement="above"
            error={fieldState.error?.message}
          />
        )}
      />
    </div>
  );
}

/** Which changes to that entity fire it. Any checked one does. */
function ChangesField({ entity }: { entity: TriggerFormEntity }) {
  const { control } = useFormContext<TriggerFormValues>();
  const offered = changesOfferedFor(entity);

  return (
    <div data-testid="trigger-changes-field">
      <Controller
        control={control}
        name="firesOn.events"
        render={({ field, fieldState }) => (
          <Checkbox.Group
            legend={
              <div>
                <span className={styles.legendRow}>
                  Change
                  <span
                    className={styles.count}
                    data-testid="trigger-changes-count"
                    data-checked={field.value.length}
                    data-total={offered.length}
                  >
                    {field.value.length} of {offered.length} checked
                  </span>
                </span>
                <span className={styles.subtitle}>
                  Select which changes start the trigger.
                </span>
              </div>
            }
            size="md"
            value={field.value}
            onValueChange={(events) => {
              field.onChange(events as PlatformEventType[]);
            }}
            ref={field.ref}
            error={fieldState.error?.message}
          >
            {offered.map((change) => (
              <Checkbox
                key={change}
                value={change}
                label={eventLabel(change)}
              />
            ))}
          </Checkbox.Group>
        )}
      />
    </div>
  );
}

/**
 * The optional condition, as one card per clause. Every card must match; with
 * none, every checked change fires the trigger.
 */
function ConditionField({
  subscription,
}: {
  subscription: EventSubscriptionValues;
}) {
  const { control, getValues, setValue } = useFormContext<TriggerFormValues>();
  const catalog = useFormTemplateCatalog();
  const definitions = useTriggerConditionDefinitions(
    catalog,
    subscription.events,
  );
  const edited = React.useRef(false);

  // Some definitions arrive after mount (metadata keys, tag directories). Until
  // the author edits, a stored condition's rows are retyped against them, so a
  // field read off its stored value takes its proper type. The rows stand for
  // a complete condition here, so the round trip loses nothing.
  React.useEffect(() => {
    const current = getValues("condition");
    if (
      edited.current ||
      current.type !== "rows" ||
      current.rows.length === 0
    ) {
      return;
    }
    setValue(
      "condition",
      conditionToValues(valuesToCondition(current), definitions),
    );
  }, [definitions, getValues, setValue]);

  return (
    <Controller
      control={control}
      name="condition"
      render={({ field, fieldState }) => {
        const count = field.value.type === "rows" ? field.value.rows.length : 0;
        return (
          <Field.Root invalid={fieldState.error?.message !== undefined}>
            <Field.Label>
              <span className={styles.legendRow}>
                Conditions
                {count > 0 && (
                  <span className={styles.count}>
                    {count} condition{count === 1 ? "" : "s"}
                  </span>
                )}
              </span>
            </Field.Label>
            <Field.Description>
              Narrow which of those changes count. Every condition must match.
              With none, the trigger fires on every change above.
            </Field.Description>
            <TriggerConditionBuilder
              value={field.value}
              onChange={(next: ConditionValues) => {
                edited.current = true;
                field.onChange(next);
              }}
              ref={field.ref}
              definitions={definitions}
              rowErrors={rowErrorsOf(fieldState.error)}
            />
            <Field.Error match>{fieldState.error?.message}</Field.Error>
          </Field.Root>
        );
      }}
    />
  );
}

/** Each card's value error, by position, off the condition's error node. */
function rowErrorsOf(error: unknown): (string | undefined)[] {
  const rows = (
    error as
      | { rows?: ({ values?: { message?: string } } | undefined)[] }
      | undefined
  )?.rows;
  return Array.from(rows ?? [], (row) => row?.values?.message);
}

/** What a custom cron expression means, in words, while it can be read. */
function cronHint(cron: string): string {
  const described = describeCron(cron);
  return described === null
    ? "Five fields, evaluated in UTC."
    : `${described} (UTC)`;
}

/** The structured schedule, and the UTC cron it is stored as. */
function ScheduleFields({ schedule }: { schedule: ScheduleValues }) {
  const { control } = useFormContext<TriggerFormValues>();
  const cron = scheduleToUtcCron(schedule);

  return (
    <div data-testid="trigger-schedule-fields" data-cron={cron ?? ""}>
      <div className={styles.grid}>
        <Controller
          control={control}
          name="firesOn.preset"
          render={({ field }) => (
            <Select<SchedulePreset>
              items={PRESET_ITEMS}
              value={field.value}
              onValueChange={(preset) => {
                if (preset !== null) {
                  field.onChange(preset);
                }
              }}
              label="Every"
              size="lg"
            />
          )}
        />

        {schedule.preset === "weekly" && (
          <Controller
            control={control}
            name="firesOn.weekday"
            render={({ field }) => (
              <Select<number>
                items={WEEKDAY_ITEMS}
                value={field.value}
                onValueChange={(weekday) => {
                  if (weekday !== null) {
                    field.onChange(weekday);
                  }
                }}
                label="On"
                size="lg"
              />
            )}
          />
        )}

        {schedule.preset !== "custom" && (
          <Controller
            control={control}
            name="firesOn.time"
            render={({ field, fieldState }) => (
              <Field.Root name={field.name} invalid={fieldState.invalid}>
                <Field.Label>At</Field.Label>
                <Field.Control
                  render={<TextInput type="time" size="lg" />}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  ref={field.ref}
                />
                <Field.Error match>{fieldState.error?.message}</Field.Error>
              </Field.Root>
            )}
          />
        )}

        {schedule.preset !== "custom" && (
          <Controller
            control={control}
            name="firesOn.timeZone"
            render={({ field }) => (
              <Combobox<string>
                items={timeZoneOptions()}
                value={field.value}
                onValueChange={(zone) => {
                  if (zone !== null) {
                    field.onChange(zone);
                  }
                }}
                label="Time zone"
                size="lg"
                autoHighlight
              >
                <Combobox.TriggerInput placeholder="Search zones" />
                <Combobox.Content>
                  <Combobox.Empty />
                  <Combobox.List>
                    {(zone: string) => (
                      <Combobox.Item key={zone} value={zone} />
                    )}
                  </Combobox.List>
                </Combobox.Content>
              </Combobox>
            )}
          />
        )}

        {schedule.preset === "custom" && (
          <Controller
            control={control}
            name="firesOn.cron"
            render={({ field, fieldState }) => (
              <Field.Root name={field.name} invalid={fieldState.invalid}>
                <Field.Label>Cron expression</Field.Label>
                <Field.Control
                  render={<TextInput spellCheck={false} size="lg" />}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  ref={field.ref}
                  placeholder="0 9 * * *"
                />
                <Field.Description>{cronHint(field.value)}</Field.Description>
                <Field.Error match>{fieldState.error?.message}</Field.Error>
              </Field.Root>
            )}
          />
        )}
      </div>
    </div>
  );
}

/**
 * What makes the trigger fire: a change to one kind of entity, optionally
 * narrowed by conditions, or a clock.
 */
export function WhenSection() {
  const firesOn = useWatch<TriggerFormValues, "firesOn">({ name: "firesOn" });
  const condition = useWatch<TriggerFormValues, "condition">({
    name: "condition",
  });

  return (
    <FormSection title="When should this trigger run">
      <RunModeField />

      {firesOn.type === TriggerSourceType.Events ? (
        <div className={styles.columns}>
          <EntityField />
          <ChangesField entity={firesOn.entity} />
        </div>
      ) : (
        isSourceComplete(firesOn) && <ScheduleFields schedule={firesOn} />
      )}

      {/* Conditions narrow the checked changes, so they wait for one. A
          schedule has none; its draft condition is kept, just not shown. */}
      {firesOn.type === TriggerSourceType.Events &&
        firesOn.events.length > 0 && (
          <div
            data-testid="trigger-conditions"
            data-has-condition={
              condition.type === "stored" || condition.rows.length > 0
            }
          >
            <ConditionField subscription={firesOn} />
          </div>
        )}
    </FormSection>
  );
}
